"use client";

import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import Cookies from "js-cookie";
import {
  selectEffectiveUser,
  selectEffectiveUserId,
  selectPrincipalUserId,
} from "../../sous-profile/redux/selectors/sousProfileSelectors";
const MessagingContext = createContext(null);

export function MessagingProvider({ children }) {
  const [selectedContact, setSelectedContact] = useState(null);
  const [profileCollapsed, setProfileCollapsed] = useState(true);

  // ✅ Shared messages from ChatWindow → consumed by ProfileSidebar
  const [sharedMessages, setSharedMessages] = useState([]);

  // ============ ROLE DETECTION FROM REDUX ============
  const doctorUser = useSelector((state) => state.doctor?.user);
  const authUser = useSelector(selectEffectiveUser);
  const effectiveUserId = useSelector(selectEffectiveUserId);
  const principalUserId = useSelector(selectPrincipalUserId);

  const userRole = useMemo(() => {
    if (doctorUser && doctorUser.id) return "doctor";
    if (authUser || effectiveUserId) return "patient";
    return "patient";
  }, [doctorUser, authUser, effectiveUserId]);

  const isDoctor = userRole === "doctor";
  const isPatient = userRole === "patient";

  const currentUserId = useMemo(() => {
    if (isDoctor && doctorUser) return String(doctorUser.id || doctorUser.doctorId || "");
    if (isPatient) return String(effectiveUserId || authUser?.id || authUser?.userId || authUser?.uuid || "");
    return null;
  }, [isDoctor, isPatient, doctorUser, authUser, effectiveUserId]);

  const formattedUserId = useMemo(() => {
    if (!currentUserId) return null;
    if (isDoctor) return currentUserId.startsWith("DR_") ? currentUserId : `DR_${currentUserId}`;
    return currentUserId;
  }, [currentUserId, isDoctor]);

  const getPeerId = useCallback(
    (contact) => {
      if (!contact) return null;
      const rawId = String(contact.id || contact.userId || contact.peerId || "");
      if (!rawId) return null;
      if (isDoctor) {
        return rawId.startsWith("DR_") ? rawId.slice(3) : rawId;
      }
      return rawId.startsWith("DR_") ? rawId : `DR_${rawId}`;
    },
    [isDoctor]
  );

  const getPeerKey = useCallback(
    (itemOrContact) => {
      if (!itemOrContact) return null;
      return String(getPeerId(itemOrContact) || "");
    },
    [getPeerId]
  );

  const STORAGE_KEY = useMemo(() => {
    return isDoctor ? "msg_lastActivity_doctor" : "msg_lastActivity_patient";
  }, [isDoctor]);

  const readMap = useCallback(() => {
    try {
      if (typeof window === "undefined") return {};
      const raw = Cookies.get(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, [STORAGE_KEY]);

  const writeMap = useCallback(
    (map) => {
      try {
        if (typeof window === "undefined") return;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
      } catch {}
    },
    [STORAGE_KEY]
  );

  const [lastActivityByPeerId, setLastActivityByPeerId] = useState(() => {
    if (typeof window === "undefined") return {};
    return readMap();
  });

  const bumpContact = useCallback(
    (peerId, ts, source = "message") => {
      if (source !== "message") return;
      if (!peerId) return;
      const key = String(peerId);
      const nextTs = Number(ts || 0);
      if (!nextTs) return;
      setLastActivityByPeerId((prev) => {
        const prevTs = Number(prev?.[key] || 0);
        if (nextTs <= prevTs) return prev;
        const next = { ...prev, [key]: nextTs };
        writeMap(next);
        return next;
      });
    },
    [writeMap]
  );

  const toggleProfile = useCallback(() => {
    setProfileCollapsed((v) => !v);
  }, []);

  const selectContact = useCallback((contact) => {
    setSelectedContact(contact);
    // Clear shared messages when switching contact
    setSharedMessages([]);
  }, []);

  const value = {
    selectedContact,
    selectContact,
    profileCollapsed,
    toggleProfile,

    userRole,
    isDoctor,
    isPatient,

    currentUserId,
    formattedUserId,
    me: formattedUserId,
    currentUser: isDoctor ? doctorUser : authUser,

    principalUserId,

    getPeerId,
    getPeerKey,

    lastActivityByPeerId,
    bumpContact,

    // ✅ shared messages between ChatWindow and ProfileSidebar
    sharedMessages,
    setSharedMessages,

    
  };

  return <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>;
}

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) throw new Error("useMessaging must be used within MessagingProvider");
  return context;
};

export default MessagingContext;
